<!-- TEMPLATE -->
<template>
    <div class="product-summary">
      <div class='row'>
        <div class='small-12 medium-12 large-12 hd-12 columns dashboard-content'>
          <dean-section-title content='true'>
            <span slot='title'>
              <span class='avenir-heavy'>Producten</span>
            </span>
            <dean-section-title-item
                    :icon='plusIcon'
                    :content="true"
                    @click.native="sectionRoute('standingData.products.add')"
            >
              Product toevoegen
            </dean-section-title-item>
          </dean-section-title>

          <div class="filter">
            <dean-form-row
                @input="update(true)"
                v-model="pageMeta.query"
                class="top-bar"
                hide-label="true"
            >zoek
            </dean-form-row>
          </div>

          <dean-table
                  :data='productData'
                  :columns="columns"
                  :hover="true"
                  @column-click='columnClick'
                  @cell-click="cellClick($event, 'standingData.products.edit')">
          </dean-table>
          <dean-pagination
                  :current-page='productsMetaData.pageNumber'
                  :total-items='productsMetaData.totalResults'
                  :items-per-page='productsMetaData.itemsPerPage'
                  :select-items-per-page='selectItemsPerPage'
                  :visible-pages='productsMetaData.visiblePages'
                  @page-changed='paginationChanged'
                  @items-per-page='itemsPerPageChanged'
          >
          </dean-pagination>
        </div>
      </div>
    </div>
</template>

<!-- SCRIPT -->
<script lang="ts">
  import { Component } from 'vue-property-decorator';
  import deanTableMixin from '@/mixins/deanTableMixin';
  import {mixins} from 'vue-class-component';
  import deanFormRow from '@/components/deanComponents/deanFormRow.vue';

  import {
    // ICONS
    plusIcon,
    // COMPONENTS
    deanSectionTitle,
    deanSectionTitleItem,
    deanTable,
    deanPagination,
  } from '@deanone/dean-components';

  @Component({
    components: {
      plusIcon,
      deanSectionTitle,
      deanSectionTitleItem,
      deanTable,
      deanPagination,
      deanFormRow,
    },
  })

  export default class List extends mixins(deanTableMixin) {
    public products: any = [];
    public plusIcon: object = plusIcon;
    public queryTimeout!: number;

    // COMPUTED
    get productData() {
      return this.products;
    }

    get productsMetaData() {
      return this.pageMeta;
    }

    // DATA
    public productSideMenu: object = {
      title: {
        text: 'Producten',
        mobile: {
          close: {
            text: 'Close',
          },
        },
      },
      filters: {
        title: 'Status',
        items: {
          active: {
            title: 'Actieve producten',
            name: 'standingData.products',
            params: {status: 'active'},
          },
          inactive: {
            title: 'Inactieve producten',
            name: 'standingData.products.status',
            params: {status: 'inactive'},
          },
          archived: {
            title: 'Gearchiveerd',
            name: 'standingData.products.status',
            params: {status: 'deleted'},
          },
        },
      },
    };

    public columns: { [name: string]: object } = {
      code: {
        content: 'Code',
        align: 'left',
        sort: true,
        dql: 'code',
      },
      privateName: {
        content: 'Omschrijving',
        align: 'left',
        sort: true,
        dql: 'privateName',
      },
      serviceName: {
        content: 'Dienst naam',
        align: 'left',
        sort: false,
        dql: 'serviceName',
      },
      groupName: {
        content: 'Grouep naam',
        align: 'left',
        sort: true,
        dql: 'groupName',
      },
      provisioningServices: {
        content: 'Provisioning Services',
        align: 'left',
        sort: false,
        dql: 'provisioningServices',
      },
      isMainProduct: {
        content: 'Hoofdproduct',
        align: 'center',
        sort: false,
        dql: 'main',
      },
      isPbxBundle: {
        content: 'PBX Bundle',
        align: 'center',
        sort: false,
        dql: 'isPbxBundle',
      },
      tags: {
        content: 'Tags',
        align: 'left',
        sort: true,
        dql: 'tags',
      },
      startDate: {
        content: 'Startdatum',
        align: 'center',
        sort: true,
        dql: 'startDate',
      },
      endDate: {
        content: 'Einddatum',
        align: 'center',
        sort: true,
        dql: 'endDate',
      },
    };

    public tableName(): string {
      return 'settings-products';
    }

    // METHODS
    public loadList(data: any): void {
      this.$store.dispatch('setLoadOverlay', true);
      this.$store.dispatch('requestProducts', data)
      .then(
        (productResponse: any) => {
          this.products = productResponse.data.data.map((product: any) => {
            return {
              id: product.id,
              code: product.code,
              privateName: product.privateName,
              serviceName: product.serviceName ? product.serviceName : '-',
              groupName: product.group ? product.group.name : '-',
              provisioningServices: product.provisioningServices ?
                  product.provisioningServices.map((service: any) => service.name).join(', ') : '-',
              isMainProduct: product.isMainProduct ? 'Ja' : 'Nee',
              isPbxBundle: product.isPbxBundle ? 'Ja' : 'Nee',
              tags: product.tags ?? '-',
              startDate: product.startDate,
              endDate: product.endDate,
            };
          });

          this.updateMeta(productResponse.data.meta);
        },
        (error: any) => {
          this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        },
      )
      .finally(
        () => {
          this.$store.dispatch('setLoadOverlay', false);
        },
      );
    }

    public cellClick(target: any): void {
      this.$router.push({
        name: 'standingData.products.edit',
        params: {id: target.row.id},
      });
    }

    public created() {
      this.keepMetaSavedInState();
      this.setDefaultSort('code');

      const data: any = this.pageMeta;
      data.status = this.$route.params.status ? this.$route.params.status : null;

      this.loadList(data);

      this.$store.dispatch('setStandingDataSideMenu', this.productSideMenu);
    }

    public update(timeout: boolean) {
      this.pageMeta.pageNumber = 1;

      const self = this;
      const meta: any = self.pageMeta;

      if (timeout) {
        if (this.queryTimeout) {
          clearTimeout(this.queryTimeout);
        }
        this.queryTimeout = setTimeout(() => {
          self.loadList(meta);
        }, 800);
      } else {
        this.loadList(meta);
      }
    }
  }
</script>
<!-- STYLE -->
<style lang="scss" scoped>
  @import '~@deanone/dean-components/src/scss/vars';

  filter {
    display: flex;
  }

  .top-bar {
    display: inline-block;
  }
</style>
